<template>
  <v-row class="fill-height paper-background video-exhibition" align="center" justify="center" no-gutters>
    <transition mode="out-in">
      <v-col v-if="!currentVideo && VideoExhibition" key="gallery" class="fill-height">
        <ClipMask class="clip-title" v-if="$vuetify.breakpoint.smAndDown" :label="VideoExhibition.title" :masked="false" />
        <Scribbles />
        <v-row class="fill-height gallery-container mx-0" align="start" justify="center">
          <v-col cols="12" md="10" offset-md="1" class="pt-16 px-6">
            <h1 class="display-2" v-if="$vuetify.breakpoint.mdAndUp"><small>{{VideoExhibition.title}}</small></h1>
          </v-col>
          <v-col cols="12" offset="0" md="9" offset-md="5" class="w-100 pt-16 mt-8 pt-md-0 mt-md-0">
            <CardGallery
              v-if="VideoExhibition"
              :data="VideoExhibition.videos"
              :cols="$vuetify.breakpoint.mdAndUp ? 5 : 10" />
          </v-col>
          <!-- <v-btn text small exact :to="{name: 'VideoExhibition', slug: VideoExhibition._slug}" class="text-script bottom-left-btn" data-cursor-target="back|center center">Info</v-btn> -->
        </v-row>
      </v-col>
      <v-col v-else-if="currentVideo" key="video" class="fill-height">
        <div class="fill-height d-flex flex-column" >
          <template v-if="$vuetify.breakpoint.smAndUp">
            <v-row class="fill-height video-room" justify="center" :style="{backgroundImage: `url(${optimizedImageURL(VideoExhibition.videoBackground, {quality: 90})})`}" >
              <!-- filler -->
              <v-col cols="12" class="align-self-start"></v-col>
              <!-- video player -->
              <v-col class="align-self-center pa-0" :style="videoStyle">
                <VuePlyr
                  style="cursor: pointer;"
                  :emit="['ended', 'timeupdate', 'exitfullscreen', 'playing', 'pause', 'enterfullscreen']"
                  :options="{fullscreen: { enabled: true }, controls}"
                  @timeupdate="videoTime = $event.detail.plyr.currentTime"
                  @enterfullscreen="fullscreen = true"
                  @exitfullscreen="fullscreen = false"
                  @playing="playing = true"
                  @pause="playing = false"
                  @ended="playing = false"
                  ref="plyr"
                >
                  <div :data-plyr-provider="currentVideo.url.indexOf('vimeo.com') !== -1 ? 'vimeo' : 'youtube'" :data-plyr-embed-id="getVideoId(currentVideo.url).id"></div>
                </VuePlyr>
              </v-col>
              <!-- title -->
              <v-col cols="12" class="align-self-end">
                <h1 class="display-1 white--text pl-13 ml-13">{{currentVideo.title}}</h1>
              </v-col>
            </v-row>

            <!-- control bar -->
            <v-row align="center" justify="start" class="flex-nowrap paper-background px-4 pt-4 pb-2">
              <v-col cols="auto" class="mx-0 px-0">
                <v-btn text :to="{name: 'VideoExhibition', params: {videoSlug: null}}" class="px-0 mx-0 no-hover-fade" data-cursor-target="default|center center|0.8"><BackIcon class="back-from-video-icon" /></v-btn>
              </v-col>
              <v-col cols="auto" class="mr-2 px-0">
                <v-btn text @click="$refs.plyr.player.togglePlay()" class="player-control-btn px-0 no-hover-fade" data-cursor-target="default|center center|0.8">
                  <transition mode="out-in">
                    <PlayIcon v-if="!playing" />
                    <PauseIcon v-else />
                  </transition>
                </v-btn>
              </v-col>
              <v-col class="grow fill-height track" @click="onTrackClick">
                <div class="progress" :style="{width: `${videoTimePerc * 100}%`}" />
              </v-col>
              <v-col cols="auto" class="mx-2 text-script text-center" :style="{fontSize: '0.6rem', width: '80px'}">{{videoTimeFormatted}}</v-col>
              <v-col cols="auto" class="mx-2"><v-btn class="player-control-btn px-0 no-hover-fade" data-cursor-target="default|center center|0.8" text @click="$refs.plyr.player.fullscreen.toggle()"><FullscreenIcon /></v-btn></v-col>
              <v-col cols="auto" class="mx-2 volume-control">
                <v-btn class="player-control-btn px-0 no-hover-fade" data-cursor-target="default|center center|0.8" text @click="$refs.plyr.player.volume === 1 ? $refs.plyr.player.decreaseVolume(1): $refs.plyr.player.increaseVolume(1)">
                  <transition mode="out-in">
                    <AudioOffIcon v-if="$refs.plyr && $refs.plyr.player.volume === 0"/>
                    <AudioIcon v-else />
                  </transition>
                </v-btn>
              </v-col>
            </v-row>
          </template>
          <!-- mobile -->
          <template v-else>
            <v-row class="fill-height video-room" justify="center" :style="{backgroundImage: `url(${optimizedImageURL(VideoExhibition.videoBackground, {quality: 90})})`}">
              <v-col class="align-self-center pa-0" :style="videoStyleMobile">
                <VuePlyr
                  style="cursor: pointer;"
                  :emit="['ended', 'timeupdate', 'exitfullscreen', 'playing', 'pause', 'enterfullscreen']"
                  :options="{fullscreen: { enabled: true }, controls}"
                  @timeupdate="videoTime = $event.detail.plyr.currentTime"
                  @enterfullscreen="fullscreen = true"
                  @exitfullscreen="fullscreen = false"
                  @playing="playing = true"
                  @pause="playing = false"
                  @ended="playing = false"
                  ref="plyr"
                >
                  <div :data-plyr-provider="currentVideo.url.indexOf('vimeo.com') !== -1 ? 'vimeo' : 'youtube'" :data-plyr-embed-id="getVideoId(currentVideo.url).id"></div>
                </VuePlyr>
                <v-col cols="12" class="align-center">
                  <h3 class="white--text w-100 text-sans text-center mx-auto">{{currentVideo.title}}</h3>
                </v-col>
                <v-row align="center" justify="center" class="">
                  <v-col cols="auto" class="mx-2 volume-control" >
                    <v-btn class="player-control-btn player-control-btn--mobile px-0 no-hover-fade" data-cursor-target="default|center center|0.8" text @click="$refs.plyr.player.volume === 1 ? $refs.plyr.player.decreaseVolume(1): $refs.plyr.player.increaseVolume(1)">
                      <transition mode="out-in">
                        <AudioOffIcon v-if="$refs.plyr && $refs.plyr.player.volume === 0"/>
                        <AudioIcon v-else />
                      </transition>
                    </v-btn>
                  </v-col>
                  <v-col cols="auto" class="mx-2">
                    <v-btn text @click="$refs.plyr.player.togglePlay()" class="player-control-btn player-control-btn--mobile px-0 no-hover-fade" data-cursor-target="default|center center|0.8">
                      <transition mode="out-in">
                        <PlayIcon v-if="!playing" />
                        <PauseIcon v-else />
                      </transition>
                    </v-btn>
                  </v-col>
                  <v-col cols="auto" class="mx-2">
                    <v-btn class="player-control-btn player-control-btn--mobile px-0 no-hover-fade" data-cursor-target="default|center center|0.8" text @click="$refs.plyr.player.fullscreen.toggle()">
                      <FullscreenIcon />
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
              <v-btn text :to="{name: 'VideoExhibition', params: {videoSlug: null}}" class="bottom-left-btn white--text text-script" data-cursor-target="back|center center">Back</v-btn>
            </v-row>
          </template>
        </div>
      </v-col>
      <v-col v-else-if="!VideoExhibition" key="loading" cols="auto">
        <div class="animated-eye" />
      </v-col>
    </transition>
  </v-row>
</template>

<script>
import GetVideoExhibition from '@/graphql/GetVideoExhibition.gql'
import CardGallery from '@/molecules/CardGallery'
import PlayIcon from '@/assets/img/play-icon.svg'
import FullscreenIcon from '@/assets/img/fullscreen-icon.svg'
import AudioIcon from '@/assets/img/audio-icon.svg'
import AudioOffIcon from '@/assets/img/audio-off-icon.svg'
import PauseIcon from '@/assets/img/pause-icon.svg'
import BackIcon from '@/assets/img/back-icon.svg'
import ClipMask from '@/molecules/ClipMask'
import Scribbles from '@/molecules/Scribbles'
import { optimizedImageURL, getLazySrc, getAspectRatio } from '@/services/OptimizedImage'
import { Viewport } from '@monogrid/vue-lib'

import getVideoId from 'get-video-id'
import find from 'lodash/find'
// import Vue from 'vue'
import VuePlyr from 'vue-plyr'

// Vue.use(VuePlyr, {
//   plyr: {
//     fullscreen: { enabled: true },
//     controls: false
//   },
//   emit: ['ended', 'timeupdate', 'exitfullscreen', 'playing', 'pause', 'enterfullscreen']
// })

export default {
  name: 'VideoExhibition',
  mixins: [Viewport],
  props: {
    slug: {
      required: true,
      type: String
    },
    videoSlug: {
      required: false,
      type: String
    }
  },
  data () {
    return {
      videoTime: 0,
      playing: false,
      fullscreen: false,
      controls: `
      <button type="button" class="plyr__control plyr__control--overlaid" data-plyr="play" aria-label="Play">
        <svg aria-hidden="true" focusable="false" width="23.7845" height="27.5" viewBox="0 0 47.569 55.036">
          <path d="M374.227 471.762c-.21.21-.2.66-.65.547l-.938-.666c-.173-.33.738-.1.614-.516l-1.3-.846c-.324-.213-.25-.285-.183-.364l-.3.3-.086-.058c-.338-.229-.893-.609-1.193-.811-.318-.219-.252-.278-.191-.338a16.538 16.538 0 0 0-1.623-1.059l-.155.135c-.042-.2-.44-.554-.942-.934.194-.209.232-.412-.767-.86l-.037.24c-1.026-.765.052-.353-.288-.561-.711-.318-1.069-.473-1.413-.669-.027.075.213.226.023.253-.351-.249-.718-.492-1.068-.712-.014-.012 0-.012-.02-.029l-.085-.037c-.5-.306-.929-.55-1.1-.646-.115-.029-.059-.173-.338-.029h-.009c-.19-.1-.224-.163-.2-.2l-.076.214c-.358-.157-1.072-.473-1.427-.627s-.338-.226-.317-.292a15.669 15.669 0 0 0-1.775-.811l-.068.246a10.826 10.826 0 0 0-3.013-2c.312.265-.752-.237-.436.033l-.57-.473-.012-.011-.017-.009c-.811-.67-1.678-1.306-2.141-1.065v.007c-.229-.122-.424-.224-.647-.338.111-.234.222-.473.327-.711.1-1.035-.813-.976-1.514-1.34-.181.351-.347.709-.511 1.066-.275-.15-.522-.281-.691-.353l-.049.086a7.447 7.447 0 0 0-1.1-.737.444.444 0 0 0 .062-.276c-.141-.068-.216-.117-.332-.175-.007-.019.013-.023 0-.045-.232-.1-.38-.176-.548-.258a1.72 1.72 0 0 1-.453-.394.874.874 0 0 0-.032.135 3.026 3.026 0 0 1-.363-.237 1.331 1.331 0 0 0 .009.312c-.089-.045-.193-.1-.27-.145.329.213-.709-.338-.381-.125-.023-.017-.056-.036-.082-.053a.476.476 0 0 0 .068-.412c-.164-.079-.406-.2-.65-.322 0-.111.012-.216.01-.328l-.009-.135v-.03l-.555-.2-.4-.148c.014-.034.014-.068.03-.1l-.043.1c-.351-.129-.676-.25-.947-.352l.115.043v.016c0 .1.009.3.007.515a1.884 1.884 0 0 0-.288-.111 1.249 1.249 0 0 0-.2.1c-.111-.05-.186-.085-.291-.135l-.114-.055c-.355-.17-.27-.387-.181-.6a6.709 6.709 0 0 1-.666-.39l.315.125-.319-.127a2.048 2.048 0 0 0-1.082-.48l-.012.026-.233-.105c.036.016.047.023.086.042l.147.063-.065.168-.068.177c-.093-.052-.163-.086-.262-.135a2.122 2.122 0 0 0-.1-.325l.433.285-.432-.285c-.027-.049-.049-.1-.078-.145.091.043.207.1.338.157-.147-.068-.234-.114-.348-.168a5.305 5.305 0 0 0-1.029-1.188c.02-.047.042-.094.065-.135.1-1.04-.813-.986-1.511-1.344-.141.266-.263.542-.388.809-.693-1.243-1.552-2.7-2.469-1.847-.163.242-.266.492-.406.737a9.681 9.681 0 0 0-1.43-.963c.271.328-.767-.219-.495.111-.816-.986-1.974-2.164-2.624-1.752-.068.081-.121.171-.184.256a11.429 11.429 0 0 0-1.306-.9c-.348-.214-.711-.426-1.042-.609v54.09a.732.732 0 0 0 .571.328c.351.03-.557.847.488.528.118-.037.247-.079.424-.148l-.075-.194c1.072-.275.148.374.567.394a9.921 9.921 0 0 1 1.339-.623c-.059-.1-.456-.105-.171-.375l1-.479c.373-.068-.226.417.157.344.328-.168.981-.509 1.309-.68s.35-.128.37-.086l-.088-.165 1.316-.668c.329-.168.347-.135.367-.1.652-.338 1.322-.653 1.632-.85l-.039-.079a14.755 14.755 0 0 0 2.394-1.086c-.082.18-.188.384.826-.324l-.088-.068c.971-.6.174.131.554-.035.522-.406.8-.626 1.086-.821.517.072.837-.059.859-.479l.3-.165c.266-.2.317-.059.365-.161a1.289 1.289 0 0 0-.145-.27l.786-.62c.292-.227.319-.22.344-.21l-.056-.019c.076-.039.128-.062.224-.111l-.049-.082c.03-.024.065-.047.1-.073.747-.286.046.27.409.207a11.807 11.807 0 0 1 1.3-.7c-.046-.078-.434-.011-.16-.276l.955-.555c.367-.109-.2.383.163.276l1.278-.744c.319-.186.341-.147.364-.107l-.092-.16c.321-.183.958-.555 1.278-.744s.344-.145.365-.106c.639-.371 1.3-.7 1.6-.927l-.068-.119a14.964 14.964 0 0 0 3.01-1.434c-.342.148.617-.409.27-.263a20.884 20.884 0 0 0 1.906-.912c-.023.112.089.112.855-.452l-.083-.068c.055-.024.135-.032.188-.059-.022-.011-.013-.027-.03-.036.588-.347.046.21.388.056.314-.262.535-.442.729-.594 1.273.466 2.272.491 1.844-.869.492-.235-.036.256.282.135.609-.416.911-.624 1.234-.8-.04-.059-.42.06-.17-.213l.9-.642c.355-.144-.16.377.188.233.3-.21.914-.623 1.217-.832s.334-.17.361-.135l-.114-.15 1.21-.839c.3-.21.338-.167.37-.124.609-.417 1.255-.783 1.522-1.036l-.112-.145a12.382 12.382 0 0 0 2.984-1.555c-.351.148.568-.473.216-.322.325-.135.682-.292 1.024-.452.763-.354 1.45-.738 1.519-1.065a5.758 5.758 0 0 0-.318-.715.9.9 0 0 0-.243-1.014c1.072-.129 2.137-.419 2.1-1.1-.1-.263.029-.5.281-.728a1.966 1.966 0 0 1-.298-.296z" transform="translate(-918.205 -633.338) translate(591.268 187.924)"/>
        </svg>
        <span class="plyr__sr-only">Play</span>
      </button>
      `
    }
  },
  components: {
    CardGallery,
    BackIcon,
    PlayIcon,
    FullscreenIcon,
    AudioIcon,
    AudioOffIcon,
    PauseIcon,
    Scribbles,
    ClipMask,
    VuePlyr
  },
  methods: {
    // onTimeUpdate (e) {
    //   console.log(e)
    // },
    // onPlaying (e) {
    //   console.log(e)
    // },
    optimizedImageURL,
    getLazySrc,
    getAspectRatio,
    getVideoId,
    onTrackClick (e) {
      const width = e.target.getBoundingClientRect().width
      const x = e.offsetX

      const perc = x / width
      this.$refs.plyr.player.currentTime = this.$refs.plyr.player.duration * perc
    }
  },
  computed: {
    videoStyleMobile () {
      const screenRatio = this.viewPort.width / this.viewPort.height
      return {
        maxWidth: screenRatio < 0.62 ? '100%' : `${this.viewPort.width * (0.62 / screenRatio)}px`,
        marginTop: `-${(0.62 - screenRatio) * 50}px`
      }
    },

    videoStyle () {
      // const backgroundRatio = 1.588 // getAspectRatio(this.VideoExhibition.videoBackground)
      const screenRatio = this.viewPort.width / this.viewPort.height

      // console.log(backgroundRatio, screenRatio)

      return {
        maxWidth: `${Math.round(screenRatio > 1.588 ? this.viewPort.width * 0.3333333333333 : (this.viewPort.height - 84) * 0.58)}px`
      }
    },
    videoTimePerc () {
      if (this.$refs.plyr) {
        return this.videoTime / this.$refs.plyr.player.duration
      }
      return this.videoTime * 0
    },
    videoTimeFormatted () {
      const minutes = Math.floor(this.videoTime / 60)
      const seconds = Math.round(this.videoTime - minutes * 60)
      return `${minutes.toString().padStart(2, '0')}.${seconds.toString().padStart(2, '0')}`
    },
    currentVideo () {
      if (this.VideoExhibition) {
        return find(this.VideoExhibition.videos, { _slug: this.videoSlug })
      }
      return null
    }
  },
  apollo: {
    VideoExhibition: {
      query: GetVideoExhibition,
      variables () {
        return {
          status: 'PUBLISHED',
          slug: this.slug
        }
      },
      result () {
        this.$root.$emit('scroll-to-saved-position')
      }
    }
  },
  metaInfo () {
    if (this.VideoExhibition) {
      let meta = [
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: `${this.VideoExhibition.title} | PhotoVogue Festival 2020` },
        { vmid: 'description', name: 'description', content: this.VideoExhibition.shareDescription },
        { vmid: 'ogdescription', property: 'og:description', content: this.VideoExhibition.shareDescription }
      ]
      if (this.VideoExhibition.shareImage) {
        meta = meta.concat([
          { vmid: 'ogimage', property: 'og:image', content: optimizedImageURL(this.VideoExhibition.shareImage, { width: 1200, height: 625, format: 'jpg', fit: 'crop' }) },
          { vmid: 'ogimagetype', property: 'og:image:type', content: 'image/jpeg' },
          { vmid: 'ogimageheight', property: 'og:image:width', content: '1200' },
          { vmid: 'ogimagewidth', property: 'og:image:height', content: '675' }
        ])
      }
      return {
        // about page browser title
        title: this.VideoExhibition.title,
        meta
      }
    }
  }
}
</script>

<style lang="scss" scoped>

::v-deep .clip-title {
  top: 80px;

  p {
    font-size: 16vw !important;
  }
}

.video-exhibition,
.gallery-container {
  width: 100vw;
  overflow-x: hidden;
}

.video-room {
  background-position: 50% 50%;
  background-size: cover;
  background-repeat: no-repeat;
}

.progress {
  position: absolute;
  height: 2px;
  background-color: map-deep-get($colors, 'red', 'base');
  top: (50px * 0.5) - 2;
  left: 0;
  transition: width 0.2s;
}

.player-control-btn {
  svg {
    fill: map-get($shades, 'black');
    transition: fill 0.25s;
  }

  &:hover {
    svg {
      fill: map-deep-get($colors, 'red', 'base');
    }
  }

  &--mobile {
    svg {
      fill: map-get($shades, 'white');
    }
  }
}

.track {
  height: 50px;
  cursor: pointer;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: (50px * 0.5) - 2;
    left: 0;
    height: 2px;
    width: 100%;
    background-color: map-deep-get($colors, 'grey', 'lighten-2');

  }
}

.back-from-video-icon {
  fill: map-deep-get($colors, 'red', 'base') !important;
  width: 35px;
  height: 35px;
}

.volume-control {
  .no-volume &,
  .mobile & {
    display: none !important;
  }
}

</style>
